import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { DateTime } from "luxon";

import { useArticlesStore } from "@/stores/Articles";
import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import { useCategoryFilter } from "@/composables/filters/category-filter";
import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { usePagingFilter } from "@/composables/filters/paging-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";

import { Article } from "@/types/entities";
import { articleKey } from "@/types/products/product-definitions";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/
export function useArticleFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const articlesStore = useArticlesStore();
    const { allArticles } = storeToRefs( articlesStore );
    const userStore = useUserStore();

    // -------------------------------- FILTER STATE --------------------------------- //
    const filterOutNonFavorites = ref( false );
    const hasAnyArticleResults = computed< boolean >( (): boolean => filteredArticles.value.length > 0 );
    const articlesSearchResultsLabel = computed< string >( (): string => `${ filteredArticles.value.length } article${ filteredArticles.value.length > 1 ? 's' : '' } found` );

    // ------------------------------------ FILTERS ------------------------------------
    // The categories that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();

    // The time interval that captures the date range for which to return articles
    const { validDateInterval, isValidDateWithinDateRange } = useDateRangeFilter();

    // The paging filter to manage what is displayed on a per-page basis
    const { filteredContentPage, pageNumber } = usePagingFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutSubscriptionBasedProducts, filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const filteredArticlesPage = computed< Article[] >(() => {
        return filteredContentPage< Article >( filteredArticles.value );
    });

    const filteredArticles = computed< Article[] >( () => {
        logger.debug( '[ article-filter ].filteredArticles -> starting computation.' );

        // Because a difference has been detected, reset the page #
        pageNumber.value = 0;

        let articlesAfterFiltering: Article[];

        logger.debug( '[ article-filter ].filteredArticles -> Date Interval filtering (START): ', validDateInterval.value.start?.toLocaleString( DateTime.DATE_FULL ) );
        logger.debug( '[ article-filter ].filteredArticles -> Date Interval filtering (END): ', validDateInterval.value.end?.toLocaleString( DateTime.DATE_FULL ) );

        const composedArticles: Article[] = allArticles.value.filter( ( article: Article ) => !filterOutNonFavorites.value || userStore.isFavorited( article.contentfulId, articleKey ) );
        if ( !isAuthenticated.value ) {
            logger.debug( `[ article-filter ].filteredArticles -> NOT logged in, showing only public articles => composedArticles: # of articles `, composedArticles.length );
            articlesAfterFiltering = composedArticles
                 // The first tier of filtering is minimizing the collection via the public/versus paywall content
                .filter( ( article: Article ) => filterOutSubscriptionBasedProducts( article.products ) )
                // Filter by date range interval
                .filter( ( article: Article ) => isValidDateWithinDateRange( article.publishedDate ) )
                // Filter by text search criteria
                .filter( ( article: Article ) => filterOutItemsThatDoNotContainSearchText( [ article.text, article.preview, article.title ] ) );
        } else {
            logger.debug( `[ article-filter ].filteredArticles -> authenticated, filtering articles based on short-code access.` );
            articlesAfterFiltering = composedArticles
                    // Filter by any categories
                    .filter( ( article: Article ) => filterOutItemsThatDoNotContainSelectedCategories( article.categories ) )
                    // Filter by any products
                    .filter( ( article: Article ) => filterOutItemsThatDoNotContainSelectedProducts( article.products ) )
                    // Filter by date range interval
                    .filter( ( article: Article ) => isValidDateWithinDateRange( article.publishedDate ) )
                    // Filter by text search criteria
                    .filter( ( article: Article ) => filterOutItemsThatDoNotContainSearchText( [ article.text, article.preview, article.title ] ) );
        }

        logger.debug( `[ article-filter ].filteredArticles -> articlesAfterFiltering : `, articlesAfterFiltering );
        return articlesAfterFiltering;
    } );

    return {
        // T/F based on if filteredArticles has any items
        hasAnyArticleResults,
        // '#### results found'
        articlesSearchResultsLabel,

        // The current single page sub-collection
        filteredArticlesPage,
        // All articles that match the current filter
        filteredArticles,
        // A flag that defines if only the user's favorite articles that match the given criteria
        // are returned or all that articles that match the criteria
        filterOutNonFavorites
    }
}

export type ArticleFilters = ReturnType< typeof useArticleFilters >;
